import { DashboardPeriodPreset, IOffer, OfferStatusFilter } from "../types/ownerDashboard.types";
import { getDashboardChartPeriodRange } from "./getDashboardChartPeriodRange";

const PENDING_OFFER_STATUSES = new Set(["CONTESTED", "PENDING"]);
const ACCEPTED_OFFER_STATUSES = new Set(["ACCEPT", "ACCEPTED"]);

function parseOfferDate(date: string): Date | null {
  const [day, month, year] = date.split("/");
  const parsed = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function parseVisitBoundaryDate(date: string, endOfDay: boolean): Date | null {
  const [month, day, year] = date.split("/");
  const parsed = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0
  );

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function matchesOfferStatus(status: string, statusFilter?: OfferStatusFilter): boolean {
  if (!statusFilter) {
    return true;
  }

  if (statusFilter === "pending") {
    return PENDING_OFFER_STATUSES.has(status);
  }

  return ACCEPTED_OFFER_STATUSES.has(status);
}

function matchesOfferPeriod(
  offerDate: string,
  periodPreset: DashboardPeriodPreset,
  historyYears?: number
): boolean {
  const parsedOfferDate = parseOfferDate(offerDate);

  if (!parsedOfferDate) {
    return true;
  }

  const periodRange = getDashboardChartPeriodRange(periodPreset, new Date(), historyYears);
  const startDate = parseVisitBoundaryDate(periodRange.initial_date, false);
  const endDate = parseVisitBoundaryDate(periodRange.final_date, true);

  if (!startDate || !endDate) {
    return true;
  }

  return parsedOfferDate >= startDate && parsedOfferDate <= endDate;
}

export function filterOffersByQuery(
  offers: IOffer[],
  options: {
    periodPreset: DashboardPeriodPreset;
    propertyId?: number | null;
    statusFilter?: OfferStatusFilter;
    historyYears?: number;
  }
): IOffer[] {
  return offers.filter((offer) => {
    if (options.propertyId != null && offer.property_id !== options.propertyId) {
      return false;
    }

    if (!matchesOfferStatus(offer.status, options.statusFilter)) {
      return false;
    }

    return matchesOfferPeriod(offer.date, options.periodPreset, options.historyYears);
  });
}

export function paginateOffersLocally(
  offers: IOffer[],
  page: number,
  perPage: number
): { items: IOffer[]; pagination: { current_page: number; per_page: number; total: number; last_page: number } } {
  const total = offers.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(page, 1), lastPage);
  const startIndex = (safePage - 1) * perPage;

  return {
    items: offers.slice(startIndex, startIndex + perPage),
    pagination: {
      current_page: safePage,
      per_page: perPage,
      total,
      last_page: lastPage,
    },
  };
}
